import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { ViewerGridMask } from "./useDetectionZones";

export const useDetectionZonesDrawings = ({
  viewerGridMask,
  cameraGridMask,
  initialState
}: {
  viewerGridMask: ViewerGridMask | null;
  cameraGridMask?: MotionDetectionZone["gridMask"] | null;
  initialState?: {
    activeIndexes?: number[] | null;
    hasUnsavedChanges?: boolean;
    drawMode?: "draw" | "erase";
  };
}) => {
  const [renderedGrid, setRenderedGrid] = useState<{ isActive: boolean; landscapeIndex: number }[]>([
    { isActive: false, landscapeIndex: 0 }
  ]);
  const [drawMode, setDrawMode] = useState<"draw" | "erase">(initialState?.drawMode || "draw");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(initialState?.hasUnsavedChanges || false);

  const isDraggingRef = useRef(false);
  const renderedGridRef = useRef<HTMLDivElement>(null);
  const lastUpdatedCellIndexRef = useRef<number | null>(null);
  const globalCleanupRef = useRef<(() => void) | null>(null);

  const lastPointRef = useRef<number | null>(null);

  const getLandscapeIndex = useCallback((index: number, rows: number, cols: number) => {
    const portraitRow = Math.floor(index / rows);
    const portraitCol = index % rows;
    const landscapeRow = rows - 1 - portraitCol;
    const landscapeCol = portraitRow;
    return landscapeRow * cols + landscapeCol;
  }, []);

  const interpolateLine = useCallback((startIndex: number, endIndex: number, numCols: number) => {
    const startRow = Math.floor(startIndex / numCols);
    const startCol = startIndex % numCols;
    const endRow = Math.floor(endIndex / numCols);
    const endCol = endIndex % numCols;

    const linePoints: number[] = [];

    // horizontal and vertical distance
    const deltaX = Math.abs(endCol - startCol);
    const deltaY = Math.abs(endRow - startRow);

    // step direction for x and y
    const stepX = startCol < endCol ? 1 : -1;
    const stepY = startRow < endRow ? 1 : -1;

    // error term to decide when to move diagonally
    let error = deltaX - deltaY;

    // current position on the grid
    let currentCol = startCol;
    let currentRow = startRow;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      // convert (row, col) back to flat index
      linePoints.push(currentRow * numCols + currentCol);

      if (currentCol === endCol && currentRow === endRow) break;

      const error2 = 2 * error;

      // adjust error and step horizontally if needed
      if (error2 > -deltaY) {
        error -= deltaY;
        currentCol += stepX;
      }

      // adjust error and step vertically if needed
      if (error2 < deltaX) {
        error += deltaX;
        currentRow += stepY;
      }
    }

    return linePoints;
  }, []);
  const updateCell = useCallback(
    (index: number) => {
      setRenderedGrid((prevGrid) =>
        prevGrid.map((cell, i) => (i === index ? { ...cell, isActive: drawMode === "draw" } : cell))
      );
      setHasUnsavedChanges(true);
    },
    [drawMode]
  );

  const batchUpdateCells = useCallback(
    (indices: number[]) => {
      if (indices.length === 0) return;

      setRenderedGrid((prevGrid) => {
        const newGrid = [...prevGrid];
        const newState = drawMode === "draw";

        indices.forEach((index) => {
          if (newGrid[index].isActive !== newState) {
            newGrid[index] = {
              ...newGrid[index],
              isActive: newState
            };
          }
        });
        return newGrid;
      });
    },
    [drawMode]
  );

  const createEmptyGrid = useCallback(
    (gridMask: ViewerGridMask, cameraRows: number, cameraCols: number) => {
      const totalCells = gridMask.rows * gridMask.cols;
      const isPortrait = gridMask.orientation === "portrait";

      return Array(totalCells)
        .fill(null)
        .map((_, index) => {
          const landscapeIndex = isPortrait ? getLandscapeIndex(index, cameraRows, cameraCols) : index;
          return {
            isActive: false,
            landscapeIndex
          };
        });
    },
    [getLandscapeIndex]
  );

  const applyExistingZones = useCallback((baseGrid: typeof renderedGrid, activeIndexes: number[]) => {
    return baseGrid.map((cell) => ({
      ...cell,
      isActive: activeIndexes.includes(cell.landscapeIndex)
    }));
  }, []);

  useEffect(() => {
    if (!viewerGridMask || !cameraGridMask) return;
    const { rows: cameraRows, cols: cameraCols } = cameraGridMask;
    const emptyGrid = createEmptyGrid(viewerGridMask, cameraRows, cameraCols);
    const activeIndexes = initialState?.activeIndexes || cameraGridMask.bits || [];
    const finalGrid = activeIndexes.length ? applyExistingZones(emptyGrid, activeIndexes) : emptyGrid;
    setRenderedGrid(finalGrid);
  }, [viewerGridMask, cameraGridMask, createEmptyGrid, applyExistingZones, initialState]);

  const clearGrid = useCallback(() => {
    setRenderedGrid((prevGrid) => prevGrid.map((cell) => ({ ...cell, isActive: false })));
    setHasUnsavedChanges(true);
    setDrawMode("draw");
  }, []);

  const getActiveIndexes = useCallback(() => {
    return renderedGrid
      .filter((cell) => cell && cell.isActive)
      .map((cell) => cell.landscapeIndex)
      .sort((a, b) => a - b);
  }, [renderedGrid]);

  const getCellFromMousePosition = useCallback(
    (e: MouseEvent | PointerEvent, gridElement: HTMLElement) => {
      if (!viewerGridMask) return null;

      const rect = gridElement.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const { rows, cols } = viewerGridMask;

      const col = Math.floor((x / rect.width) * cols);
      const row = Math.floor((y / rect.height) * rows);

      if (row >= 0 && row < rows && col >= 0 && col < cols) {
        return row * cols + col;
      }
      return null;
    },
    [viewerGridMask]
  );

  const areNeighborCells = useCallback((index1: number, index2: number, cols: number) => {
    const row1 = Math.floor(index1 / cols);
    const col1 = index1 % cols;
    const row2 = Math.floor(index2 / cols);
    const col2 = index2 % cols;

    const rowDiff = Math.abs(row2 - row1);
    const colDiff = Math.abs(col2 - col1);

    return rowDiff <= 1 && colDiff <= 1;
  }, []);

  const continueDrawing = useCallback(
    (index: number) => {
      if (!isDraggingRef.current || !viewerGridMask) return;

      if (lastPointRef.current !== null && lastPointRef.current !== index) {
        if (areNeighborCells(lastPointRef.current, index, viewerGridMask.cols)) {
          updateCell(index);
        } else {
          log.detectionZones("About to call interpolateLine", lastPointRef.current, index, viewerGridMask.cols);
          const interpolatedPoints = interpolateLine(lastPointRef.current, index, viewerGridMask.cols);
          batchUpdateCells(interpolatedPoints);
        }
      }
      lastPointRef.current = index;
    },
    [viewerGridMask, interpolateLine, updateCell, areNeighborCells, batchUpdateCells]
  );

  const stopDrag = useCallback(() => {
    isDraggingRef.current = false;
    lastUpdatedCellIndexRef.current = null;
    lastPointRef.current = null;

    globalCleanupRef.current?.();
    globalCleanupRef.current = null;
  }, []);

  const startDrag = useCallback(
    (index: number) => {
      if (!viewerGridMask) return;
      log.detectionZones("Start drag on cell", index);
      isDraggingRef.current = true;
      lastUpdatedCellIndexRef.current = null;
      lastPointRef.current = index;
      updateCell(index);

      const handleMove = (e: PointerEvent) => {
        if (!renderedGridRef.current) return;
        const cellIndex = getCellFromMousePosition(e, renderedGridRef.current);
        if (cellIndex !== null) continueDrawing(cellIndex);
      };

      const handleEnd = () => stopDrag();

      document.addEventListener("pointermove", handleMove);
      document.addEventListener("pointerup", handleEnd);
      document.addEventListener("pointercancel", handleEnd);

      globalCleanupRef.current = () => {
        document.removeEventListener("pointermove", handleMove);
        document.removeEventListener("pointerup", handleEnd);
        document.removeEventListener("pointercancel", handleEnd);
      };
    },
    [viewerGridMask, updateCell, getCellFromMousePosition, continueDrawing, stopDrag]
  );

  const handleClick = useCallback(
    (index: number) => {
      if (!isDraggingRef.current) updateCell(index);
    },
    [updateCell]
  );

  const cellDimensions = useMemo(() => {
    if (!viewerGridMask) return { cellWidth: 0, cellHeight: 0 };
    const { cols } = viewerGridMask;
    const canvasSizeInPercent = 100;

    return {
      cellWidth: canvasSizeInPercent / cols,
      cellHeight: canvasSizeInPercent / viewerGridMask.rows
    };
  }, [viewerGridMask]);

  useEffect(() => {
    return () => {
      globalCleanupRef.current?.();
    };
  }, []);

  return {
    renderedGridRef,
    renderedGrid,
    cellDimensions,
    drawMode,
    hasUnsavedChanges,
    getActiveIndexes,
    setDrawMode,
    clearGrid,
    handleClick,
    startDrag
  };
};
